import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageMeta } from '../shared/PageMeta';
import { AccountRequestsList } from './AccountRequestsList';
import { UserReviewList } from './UserReviewList';
import { DonationVerifyList } from './DonationVerifyList';
import { ContactSubmissionsList } from './ContactSubmissionsList';
import { ReferenceListManager } from './ReferenceListManager';
import { SchemaVisualization } from './SchemaVisualization';
import { SeedDataManager } from './SeedDataManager';
import { usePendingAccountRequestsCount } from '../../../api/admin';

function TabBar({ tabs, activeTab, onSwitch }: { tabs: { id: string; label: string; badge?: number }[]; activeTab: string; onSwitch: (id: string) => void }) {
  return (
    <div className="flex gap-1 mb-6 bg-spi-seed rounded-xl p-1 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onSwitch(tab.id)}
          className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === tab.id ? 'bg-white shadow text-spi-green-dark' : 'text-spi-slate hover:text-spi-charcoal'
          }`}
        >
          {tab.label}
          {tab.badge != null && tab.badge > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'applications');
  const { data: pendingCount } = usePendingAccountRequestsCount();

  const tabs = [
    { id: 'applications', label: 'Applications', badge: pendingCount },
    { id: 'users', label: 'Users' },
    { id: 'transactions', label: 'Donations' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'lists', label: 'Reference Lists' },
    { id: 'schema', label: 'Schema' },
    { id: 'demo', label: 'Demo Data' },
  ];

  const switchTab = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title="Admin Panel" description="Manage marketplace users and donations" path="/marketplace/admin" />
      <h2 className="text-3xl mb-6">Admin Panel</h2>

      <TabBar tabs={tabs} activeTab={activeTab} onSwitch={switchTab} />

      {activeTab === 'applications' && <AccountRequestsList />}
      {activeTab === 'users' && <UserReviewList />}
      {activeTab === 'transactions' && <DonationVerifyList />}
      {activeTab === 'contacts' && <ContactSubmissionsList />}
      {activeTab === 'lists' && <ReferenceListManager />}
      {activeTab === 'schema' && <SchemaVisualization />}
      {activeTab === 'demo' && <SeedDataManager />}
    </div>
  );
}
